import { useEffect, useRef } from 'react';

/**
 * Neural orb — a rotating 3D sphere of glowing nodes connected by synapse edges,
 * pulsing over a bright radial glow. Ported from the Beacon project's "Ask Beacon"
 * launcher mark (#222) so the Stratus Copilot shares the same living-orb look.
 * Canvas-rendered; ALWAYS animates (the moving glow is the whole point).
 */
const PALETTE = ['#22d3ee', '#38bdf8', '#60a5fa', '#818cf8', '#a78bfa', '#2dd4bf', '#e0f2fe'];

export function Orb({ size = 34, strong = false }: { size?: number; strong?: boolean }) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    const N = 26;
    const nodes = Array.from({ length: N }, (_, i) => {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const phi = i * Math.PI * (3 - Math.sqrt(5));
      return { x: Math.cos(phi) * r, y, z: Math.sin(phi) * r, c: PALETTE[i % PALETTE.length] };
    });
    const edges: [number, number][] = [];
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dz = nodes[i].z - nodes[j].z;
        if (dx * dx + dy * dy + dz * dz < 0.62) edges.push([i, j]);
      }
    }

    let raf = 0;
    let t = strong ? 0 : 0.8;
    let mounted = true;

    const frame = () => {
      if (!mounted) return;
      t += 0.012;
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      const rad = w * 0.3;
      const pulse = 1 + 0.08 * Math.sin(t * 2.2);

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      const gr = w * (0.34 + 0.03 * Math.sin(t * 2.2));
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, gr);
      g.addColorStop(0, `rgba(120,200,255,${strong ? 0.5 : 0.42})`);
      g.addColorStop(0.4, 'rgba(90,130,255,0.16)');
      g.addColorStop(1, 'rgba(90,130,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, gr, 0, Math.PI * 2);
      ctx.fill();

      const ay = t;
      const ax = 0.42 + 0.18 * Math.sin(t * 0.55);
      const cosY = Math.cos(ay);
      const sinY = Math.sin(ay);
      const cosX = Math.cos(ax);
      const sinX = Math.sin(ax);
      const proj = nodes.map((n) => {
        const x1 = n.x * cosY - n.z * sinY;
        const z1 = n.x * sinY + n.z * cosY;
        const y2 = n.y * cosX - z1 * sinX;
        const z2 = n.y * sinX + z1 * cosX;
        return { sx: cx + x1 * rad * pulse, sy: cy + y2 * rad * pulse, depth: (z2 + 1) / 2 };
      });

      ctx.lineCap = 'round';
      for (const [i, j] of edges) {
        const a = proj[i];
        const b = proj[j];
        const d = (a.depth + b.depth) / 2;
        ctx.strokeStyle = `rgba(150,190,255,${0.12 + 0.4 * d})`;
        ctx.lineWidth = (0.5 + 1.1 * d) * dpr;
        ctx.beginPath();
        ctx.moveTo(a.sx, a.sy);
        ctx.lineTo(b.sx, b.sy);
        ctx.stroke();
      }

      const order = proj.map((p, i) => ({ p, i })).sort((u, v) => u.p.depth - v.p.depth);
      for (const { p, i } of order) {
        const twinkle = 0.75 + 0.25 * Math.sin(t * 3 + i * 1.7);
        const r = (1.0 + 1.9 * p.depth) * dpr * twinkle;
        ctx.shadowBlur = (0.04 + 0.05 * p.depth) * w * (strong ? 1.15 : 1);
        ctx.shadowColor = nodes[i].c;
        ctx.globalAlpha = 0.5 + 0.5 * p.depth;
        ctx.fillStyle = nodes[i].c;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 0.6 + 0.4 * p.depth;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, Math.max(0.5, r * 0.4), 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(frame);
    };

    frame();
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
    };
  }, [size, strong]);

  return (
    <canvas
      ref={ref}
      style={{ width: size, height: size, display: 'block', flex: '0 0 auto' }}
      aria-hidden="true"
    />
  );
}

export default Orb;
